import React, { useEffect, useState } from 'react'
import './App.css'
import './FeesPage.css'
import { Link, Redirect } from 'react-router-dom'
import Header from './layouts/Header'

const FEES = [
  ['Event creation', 'Free', 'Create unlimited events whenever you need.'],
  ['Upfront cost', 'R0', 'No campaign budget or deposit required to start selling.'],
  ['Service fee', '3.5%', 'Deducted from every ticket sale.', true],
  ['Self-print ticket', 'R1.00', 'Download your ticket and print it yourself.'],
  ['Karamale printing', 'R3.50', 'Karamale prints and prepares physical tickets.'],
  ['Payout', 'Small fee', 'A small fee applies when funds are transferred to your account.'],
]

const PRINCIPLES = [
  ['Create for free', 'There is no charge to create an event or prepare it for launch.'],
  [
    'Nothing upfront',
    'No budget to load and no deposit. Start selling as soon as your event is ready.',
  ],
  ['Pay as you sell', 'Karamale deducts 3.5% from every ticket sale. No sales, no service fee.'],
]

const FORMATS = [
  [
    'Digital',
    'Electronic ticket',
    'Issued electronically',
    '3.5%',
    'Of each sale',
    'Automatically deducted when the ticket is sold.',
  ],
  [
    'Physical',
    'Self-print',
    'Download + print',
    'R1.00',
    'Per ticket',
    'Download the ticket and print it yourself.',
  ],
  [
    'Physical',
    'Karamale print',
    'Printed by Karamale',
    'R3.50',
    'Per ticket',
    'Karamale prints and prepares tickets for distribution.',
  ],
]

const BUYERS = [
  ['Small booking fee', 'A small booking fee may be added at checkout.'],
  ['No processing surcharge', 'There are no additional payment-processing charges.'],
  ['Upfront pricing', 'Applicable fees are displayed before the buyer pays.'],
]

const STEPS = [
  ['Create your event', 'No event creation fee.'],
  ['Start selling', 'No budget or deposit needed.'],
  ['Karamale takes 3.5%', 'Deducted automatically from every ticket sale.'],
  ['Receive your payout', 'A small fee applies when funds are transferred.'],
]

const PROMOTER_POINTS = [
  'Create unique login credentials for each promoter',
  'Sell tickets directly on your behalf',
  'Track sales and earnings',
  'View category-level sales breakdowns',
  'Deactivate or delete promoter accounts',
]

const FAQS = [
  {
    id: 'free',
    question: 'Is it free to create events?',
    answer: 'Yes. Creating an event on Karamale is completely free.',
  },
  {
    id: 'upfront',
    question: 'Do I need to pay anything upfront?',
    answer:
      'No. There is no budget to load and no deposit to make. Create your event and start selling.',
  },
  {
    id: 'fee',
    question: 'How does Karamale make its money?',
    answer:
      'Karamale deducts a 3.5% service fee from every ticket sale. If you do not sell, you do not pay the service fee.',
  },
  {
    id: 'printed',
    question: 'Can I sell printed tickets only?',
    answer:
      'Yes. Karamale supports both printed and digital tickets, with fraud protection and scanning validation.',
  },
]

const FeeCard = ({ label, value, description, featured }) => (
  <article className={`fp-fee ${featured ? 'fp-fee--hot' : ''}`}>
    <div className="fp-fee__top">
      <span>{label}</span>
      {featured && <b>Core fee</b>}
    </div>
    <strong>{value}</strong>
    <p>{description}</p>
  </article>
)

const Eyebrow = ({ children }) => (
  <span className="fp-pill">
    <i /> {children}
  </span>
)

const FeesPage = () => {
  const [openSection, setOpenSection] = useState(null)

  useEffect(() => {
    document.title = 'Fees & Pricing - Karamale'
  }, [])

  if (localStorage.getItem('customername') !== null) {
    return <Redirect to="/customerdashboard" />
  }

  const toggleSection = (section) => {
    setOpenSection((current) => (current === section ? null : section))
  }

  return (
    <div className="fp-page">
      <Header />

      {/* HERO */}
      <section className="fp-hero">
        <div className="fp-aurora" aria-hidden="true">
          <span className="fp-aurora__a" />
          <span className="fp-aurora__b" />
        </div>
        <div className="fp-wrap fp-hero__inner">
          <div>
            <Eyebrow>Karamale pricing</Eyebrow>
            <h1>
              Simple pricing.
              <br />
              <em>No surprises.</em>
            </h1>
            <p className="fp-hero__sub">
              Create events for free and pay nothing upfront. Karamale only takes 3.5% when you sell
              a ticket.
            </p>
            <div className="fp-actions">
              <Link to="/organiser-registration" className="fp-btn fp-btn--brand">
                Start selling
              </Link>
              <a href="#organiser-fees" className="fp-btn fp-btn--ghost">
                See the pricing
              </a>
            </div>
          </div>

          <div className="fp-hero__visual">
            <div className="fp-bigcard">
              <small>Upfront cost</small>
              <strong>R0</strong>
              <div className="fp-bigcard__line">Pay only when you sell.</div>
            </div>
            <div className="fp-mini fp-mini--one">
              <small>Service fee</small>
              <strong>3.5%</strong>
              <span>deducted from every ticket sale</span>
            </div>
            <div className="fp-mini fp-mini--two">
              <small>Event creation</small>
              <strong>Free</strong>
              <span>create as many events as you need</span>
            </div>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="fp-section">
        <div className="fp-wrap">
          <h2 className="fp-h2 fp-h2--solo">
            Three things <span>to remember.</span>
          </h2>
          <div className="fp-cols">
            {PRINCIPLES.map(([title, text], i) => (
              <div key={title} className={`fp-card ${i === 2 ? 'fp-card--hot' : ''}`}>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ORGANISER FEES */}
      <section className="fp-section" id="organiser-fees">
        <div className="fp-wrap">
          <div className="fp-head">
            <div>
              <Eyebrow>For organisers</Eyebrow>
              <h2 className="fp-h2 fp-h2--mt">
                Know exactly <span>what you're paying for.</span>
              </h2>
            </div>
            <p>
              Creating your event is free and there is nothing to pay upfront. Karamale takes a 3.5%
              share of each ticket sale, while printing and payouts have clearly defined costs.
            </p>
          </div>
          <div className="fp-cols">
            {FEES.map(([label, value, description, featured]) => (
              <FeeCard
                key={label}
                label={label}
                value={value}
                description={description}
                featured={featured}
              />
            ))}
          </div>
          <div className="fp-note">
            <span className="fp-tick" aria-hidden="true" />
            <div>
              <strong>You only pay when you sell.</strong>
              <p>
                There is nothing to load upfront. Karamale's 3.5% is deducted automatically from
                each ticket sale.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TICKET FORMATS */}
      <section className="fp-section">
        <div className="fp-wrap">
          <div className="fp-head">
            <div>
              <Eyebrow>Ticket formats</Eyebrow>
              <h2 className="fp-h2 fp-h2--mt">
                Digital, <span>physical, or both.</span>
              </h2>
            </div>
            <p>
              Choose how your audience receives their tickets. Every format is protected and
              validated through Karamale.
            </p>
          </div>
          <div className="fp-table" role="table">
            <div className="fp-table__head" role="row">
              <span>Format</span>
              <span>Delivery</span>
              <span>Cost</span>
              <span>Details</span>
            </div>
            {FORMATS.map(([name, sub, delivery, cost, per, details]) => (
              <div className="fp-table__row" role="row" key={sub}>
                <div data-label="Format">
                  <strong>{name}</strong>
                  <small>{sub}</small>
                </div>
                <div data-label="Delivery">{delivery}</div>
                <div data-label="Cost">
                  <strong className="fp-red">{cost}</strong>
                  <small>{per}</small>
                </div>
                <div data-label="Details">{details}</div>
              </div>
            ))}
          </div>
          <p className="fp-foot">
            <span className="fp-tick" aria-hidden="true" />
            All ticket formats include fraud protection and scanning validation.
          </p>
        </div>
      </section>

      {/* BUYERS */}
      <section className="fp-section">
        <div className="fp-wrap">
          <div className="fp-head">
            <div>
              <Eyebrow>For ticket buyers</Eyebrow>
              <h2 className="fp-h2 fp-h2--mt">
                Clear pricing <span>at checkout.</span>
              </h2>
            </div>
            <p>
              Buyers see applicable fees before completing their purchase. No unexpected
              payment-processing charges are added afterwards.
            </p>
          </div>
          <div className="fp-cols">
            {BUYERS.map(([title, text], i) => (
              <div key={title} className={`fp-card ${i === 1 ? 'fp-card--hot' : ''}`}>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="fp-section">
        <div className="fp-wrap">
          <div className="fp-flow">
            <div className="fp-flow__copy">
              <Eyebrow>How it works</Eyebrow>
              <h2 className="fp-h2 fp-h2--mt">
                Sell first. <span>Pay as you go.</span>
              </h2>
              <p className="fp-lead">
                Creating an event is free and you don't load any funds. Karamale's share is taken
                from each sale as it happens.
              </p>
              <div className="fp-zero">
                <small>Upfront cost</small>
                <strong>R0</strong>
              </div>
            </div>
            <ol className="fp-steps">
              {STEPS.map(([title, text], i) => (
                <li key={title}>
                  <span className="fp-steps__n">{i + 1}</span>
                  <div>
                    <strong>{title}</strong>
                    <p>{text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* PROMOTERS */}
      <section className="fp-section">
        <div className="fp-wrap fp-two">
          <div>
            <Eyebrow>Promoter accounts</Eyebrow>
            <h2 className="fp-h2 fp-h2--mt">
              Give your team <span>their own lane.</span>
            </h2>
            <p className="fp-lead">
              Create individual promoter accounts and give your sales team the ability to sell
              tickets on your behalf.
            </p>
            <Link to="/organiser-registration" className="fp-btn fp-btn--brand">
              Become an organiser
            </Link>
          </div>
          <div className="fp-panel">
            <div className="fp-panel__head">
              <span>Promoter access</span>
              <b>
                <i /> Live
              </b>
            </div>
            <ul className="fp-checks">
              {PROMOTER_POINTS.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="fp-section">
        <div className="fp-wrap fp-faq">
          <div>
            <Eyebrow>Questions</Eyebrow>
            <h2 className="fp-h2 fp-h2--mt">
              Frequently asked <span>questions.</span>
            </h2>
          </div>
          <div className="fp-faq__list">
            {FAQS.map((item) => {
              const open = openSection === item.id
              return (
                <div key={item.id} className={`fp-faq__item ${open ? 'is-open' : ''}`}>
                  <button type="button" aria-expanded={open} onClick={() => toggleSection(item.id)}>
                    <span>{item.question}</span>
                    <b aria-hidden="true">{open ? '−' : '+'}</b>
                  </button>
                  {open && <div className="fp-faq__answer">{item.answer}</div>}
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="fp-section fp-section--last">
        <div className="fp-wrap">
          <div className="fp-cta">
            <h2>
              Your next event <em>starts here.</em>
            </h2>
            <p>Create your organiser account and start building your event today.</p>
            <div className="fp-actions fp-actions--center">
              <Link to="/organiser-registration" className="fp-btn fp-btn--light">
                Create organiser account
              </Link>
              <Link to="/registration" className="fp-btn fp-btn--outline">
                Explore Karamale
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default FeesPage
